import { createContext, useContext, useInsertionEffect, useRef, useState, type ReactNode } from 'react'
import type { UFile } from '@w/utils'
import type { UploadCallbacks } from '../../upload'

/** CoreEditor supplies the same renderer and transaction boundary to compound block fields. */
export interface BlockViewServices {
  updateBlock: (id: string, patch: Record<string, unknown>) => void
  upload: (files: UFile[], callbacks: UploadCallbacks) => Promise<void>
  /** BlockEditorProps.renderQuestionSettings; undefined when the host supplies none. */
  renderQuestionSettings?: (qid: string) => ReactNode
}

const ServicesContext = createContext<Omit<BlockViewServices, 'renderQuestionSettings'> | null>(null)
/** Called during render, so it is read as the current value (a mode switch or a card change re-renders its readers). */
const QuestionSettingsContext = createContext<BlockViewServices['renderQuestionSettings']>(undefined)

/**
 * Event services keep one identity for the editor's life: handlers call them (never render), and each call reaches the
 * latest functions, written before any layout effect runs (the `useEffectEvent` polyfill: React RFC 0220 `useEvent`).
 * A value that changed on every editor render made React search every memoized row for consumers on each keystroke.
 */
export function BlockViewProvider({ value, children }: { value: BlockViewServices; children: ReactNode }) {
  const latest = useRef(value)
  useInsertionEffect(() => { latest.current = value })
  const [services] = useState<Omit<BlockViewServices, 'renderQuestionSettings'>>(() => ({
    updateBlock: (id, patch) => { latest.current.updateBlock(id, patch) },
    upload: (files, callbacks) => latest.current.upload(files, callbacks),
    ...value.renderQuestionSettings ? { renderQuestionSettings: (qid: string) => latest.current.renderQuestionSettings?.(qid) } : {},
  }))
  return <ServicesContext value={services}><QuestionSettingsContext value={value.renderQuestionSettings}>{children}</QuestionSettingsContext><style>{`
    [data-cb-field-placeholder][data-question-field]::after,
    [data-cb-field-placeholder][data-cloze]::after {
      content: attr(data-cb-field-placeholder);
      pointer-events: none;
      color: var(--color-on-surface-secondary);
    }
    .cb-paragraph[data-cb-field-placeholder]::before {
      content: attr(data-cb-field-placeholder);
      margin-inline-end: 0.5em;
      pointer-events: none;
      color: var(--color-on-surface-secondary);
    }
  `}</style></ServicesContext>
}

export function useBlockViewServices(): BlockViewServices {
  const services = useContext(ServicesContext)
  const renderQuestionSettings = useContext(QuestionSettingsContext)
  if (!services) throw new Error('CoreEditor must provide BlockViewProvider for compound block fields')
  return renderQuestionSettings ? { ...services, renderQuestionSettings } : services
}
